<template>
<div class="training">
    <p class="pfad">Übersicht</p>
    <h1>Was möchtest du üben?</h1>
    <p class="einleitung">Beide Bereiche sind von oben nach unten aufgebaut: erst ansehen, dann selbst machen.</p>

    <div class="module">

        <!-- Funkgeraet: die Modi sind eine Abfolge, darum nummeriert. -->
        <section class="modul" aria-labelledby="modul-funk">
            <header class="modul__kopf">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M9 2.5l1.5 5.5" /><rect x="6" y="8" width="12" height="13.5" rx="1.5" /><path d="M9 12.5h6M9 15.5h6M9 18.5h3" />
                </svg>
                <div>
                    <h2 id="modul-funk">Funkgerät</h2>
                    <p>Das Handfunkgerät kennen und bedienen, bevor es in der Hand liegt.</p>
                </div>
            </header>
            <ol class="weg">
                <li v-for="(mode, i) in modes" :key="mode.id">
                    <RouterLink :to="{name: 'Funkgeraet', params: {mode: mode.id}}" class="schritt">
                        <span class="schritt__nr" aria-hidden="true">{{ i + 1 }}</span>
                        <span class="schritt__text">
                            <span class="titel">{{ mode.label }}</span>
                            <span class="beschreibung">{{ mode.description }}</span>
                        </span>
                        <span class="pfeil" aria-hidden="true">→</span>
                    </RouterLink>
                </li>
            </ol>
        </section>

        <!-- Sprechregeln: die vier Stufen, jede mit ihren Uebungsarten als
             direkte Links. -->
        <section class="modul" aria-labelledby="modul-sprech">
            <header class="modul__kopf">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H10l-6 4z" /></svg>
                <div>
                    <h2 id="modul-sprech">Sprechregeln</h2>
                    <p>Abläufe aus dem Behelf ordnen, beurteilen und selbst funken.</p>
                </div>
            </header>
            <ol class="weg">
                <li v-for="(st, i) in stufen" :key="st.id" class="stufe">
                    <span class="schritt__nr" aria-hidden="true">{{ i + 1 }}</span>
                    <span class="schritt__text">
                        <span class="titel">{{ st.name }}</span>
                        <span class="arten">
                            <RouterLink v-for="a in uebungsarten.filter(a => a.stufe === st.id)" :key="a.id"
                                :to="{ path: '/sprechregeln', query: { art: a.id } }" class="art">{{ a.name }}</RouterLink>
                        </span>
                    </span>
                </li>
            </ol>
        </section>

    </div>

    <section class="unterricht" aria-labelledby="titel-unterricht">
        <h2 id="titel-unterricht">Im Unterricht</h2>
        <nav class="unterricht__karten">
            <RouterLink :to="{name: 'SitzungBeitreten'}" class="karte">
                <span class="titel">Sitzung beitreten</span>
                <span class="beschreibung">Mit dem Code deines Ausbildners.</span>
            </RouterLink>
            <RouterLink :to="{name: 'SitzungLeiten'}" class="karte">
                <span class="titel">Sitzung leiten</span>
                <span class="beschreibung">Für Ausbildner: Code an die Klasse geben und den Stand sehen.</span>
            </RouterLink>
        </nav>
    </section>
</div>
</template>

<script>
import { modes } from '@/data/Modes';
import { stufen, uebungsarten } from '@/data/Uebungsarten';

export default {
    setup() {
        // Der Sitzungsmodus erscheint hier nicht — man kommt nur ueber "Sitzung beitreten" hinein.
        const trainingsModes = modes.filter(m => !m.features.sitzung)

        return { modes: trainingsModes, stufen, uebungsarten }
    }
}

</script>

<style scoped>
/* Uebersicht als Lernweg: zwei Bereiche als Tafeln, innen nummerierte
   Schritte mit einer Linie dazwischen. #app zentriert global allen Text —
   hier wieder abwaehlen. */
.training {
    max-width: 1080px;
    padding: 2.25rem 2.5rem 3rem;
    text-align: left;
}

.pfad {
    margin: 0 0 0.35rem;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 750;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--zs-tinte);
}

.einleitung {
    max-width: 60ch;
    margin: 0.5rem 0 2rem;
    font-size: 1.05rem;
    color: var(--zs-grau);
}

.module {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(22rem, 1fr));
    gap: 1.25rem;
    align-items: start;
}

.modul {
    padding: 1.5rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
}

.modul__kopf {
    display: flex;
    gap: 0.9rem;
    align-items: flex-start;
    margin-bottom: 1.25rem;
}

.modul__kopf svg {
    flex: none;
    width: 2.6rem;
    height: 2.6rem;
    padding: 0.55rem;
    fill: none;
    stroke: var(--zs-blau);
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
    background: var(--zs-blau-hell);
    border-radius: var(--zs-ecke);
    box-sizing: border-box;
}

.modul h2 {
    margin: 0.1rem 0 0.2rem;
    font-size: 1.3rem;
    font-weight: 750;
    letter-spacing: -0.01em;
}

.modul__kopf p {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
    color: var(--zs-grau);
}

/* ─── Der Weg: Nummer, Linie zur naechsten Nummer ─────────── */

.weg {
    margin: 0;
    padding: 0;
    list-style: none;
}

.weg > li {
    position: relative;
}

/* Verbindungslinie von Nummer zu Nummer. */
.weg > li:not(:last-child)::after {
    content: '';
    position: absolute;
    left: calc(0.75rem + 0.9rem - 1px);
    top: 2.6rem;
    bottom: -0.35rem;
    width: 2px;
    background: var(--zs-rahmen);
    pointer-events: none;
}

.schritt,
.stufe {
    display: flex;
    gap: 0.9rem;
    align-items: flex-start;
    padding: 0.75rem;
    color: var(--zs-tinte);
    text-decoration: none;
    border-radius: var(--zs-ecke);
}

.schritt {
    transition: background-color 0.15s;
}

.schritt:hover {
    background: var(--zs-flaeche);
}

.schritt:focus-visible,
.art:focus-visible,
.karte:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

.schritt__nr {
    position: relative;
    z-index: 1;
    display: grid;
    place-items: center;
    flex: none;
    width: 1.8rem;
    height: 1.8rem;
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--zs-blau);
    background: var(--zs-blau-hell);
    border-radius: 50%;
}

.schritt__text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
}

.titel {
    font-size: 1.02rem;
    font-weight: 650;
}

.beschreibung {
    font-size: 0.92rem;
    line-height: 1.45;
    color: var(--zs-grau);
}

.pfeil {
    align-self: center;
    color: var(--zs-grau);
    transition: transform 0.15s, color 0.15s;
}

.schritt:hover .pfeil {
    color: var(--zs-blau);
    transform: translateX(3px);
}

/* Uebungsarten einer Stufe als kleine Links nebeneinander. */
.arten {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.35rem;
}

.art {
    padding: 0.3rem 0.65rem;
    font-size: 0.88rem;
    font-weight: 500;
    color: var(--zs-blau);
    text-decoration: none;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: 999px;
    transition: background-color 0.15s, border-color 0.15s;
}

.art:hover {
    background: var(--zs-blau-hell);
    border-color: var(--zs-blau-mittel);
}

/* ─── Unterricht ─────────────────────────────────────────── */

.unterricht {
    margin-top: 2.25rem;
}

.unterricht h2 {
    margin: 0 0 0.75rem;
    font-size: 0.8rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.unterricht__karten {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
    gap: 1rem;
}

.karte {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: 1.1rem 1.25rem;
    color: var(--zs-tinte);
    text-decoration: none;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
    transition: box-shadow 0.15s, border-color 0.15s;
}

.karte:hover {
    border-color: var(--zs-blau-mittel);
    box-shadow: var(--zs-schatten-hoch);
}

@media (max-width: 900px) {
    .training {
        padding: 1.5rem 16px 2.5rem;
    }
}

@media (prefers-reduced-motion: reduce) {
    .schritt,
    .pfeil,
    .art,
    .karte {
        transition: none;
    }
}
</style>
